import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import AssignCCPModal from '../components/AssignCCPModal';
import { FileText, Upload, Trash2, Eye, Play, Loader, CheckCircle, XCircle, Clock, X, AlertCircle, Search } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import { tarionDocumentService, TarionDocument } from '../services/tarionDocumentService';
import { ccpDocumentService, CCPDocument } from '../services/ccpDocumentService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/PortalSettings.css';
import '../styles/Contacts.css';

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

const ServiceDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [reloadingDocuments, setReloadingDocuments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<'tarion' | 'ccp'>('tarion');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [tarionDocuments, setTarionDocuments] = useState<TarionDocument[]>([]);
  const [ccpDocuments, setCcpDocuments] = useState<CCPDocument[]>([]);
  const [ccpPagination, setCcpPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [ccpSearchQuery, setCcpSearchQuery] = useState('');
  const [ccpDebouncedSearchQuery, setCcpDebouncedSearchQuery] = useState('');
  const [processingDocuments, setProcessingDocuments] = useState<Set<string>>(new Set());
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgressList, setUploadProgressList] = useState<UploadProgress[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const [pdfModal, setPdfModal] = useState<{
    isOpen: boolean;
    url: string;
    fileName: string;
  }>({
    isOpen: false,
    url: '',
    fileName: ''
  });
  const [assignCCPModal, setAssignCCPModal] = useState<{
    isOpen: boolean;
    documentId: string;
    documentFileName: string;
  }>({
    isOpen: false,
    documentId: '',
    documentFileName: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      if (!workspaceId) return;

      try {
        const response = await projectService.getAll(workspaceId);
        setProjects(response.projects);

        // Set the first project as default
        if (response.projects.length > 0) {
          setSelectedProjectId(response.projects[0]._id);
        }
      } catch (error: any) {
        console.error('Error loading projects:', error);
        toast.error('Failed to load projects');
      }
    };

    loadProjects();
  }, [workspaceId]);

  // Track if initial load has happened
  const initialLoadRef = useRef(true);

  // Tarion: reload on tab/project/workspace changes
  useEffect(() => {
    if (!workspaceId || !selectedProjectId) {
      setLoading(false);
      return;
    }
    if (activeTab === 'tarion') {
      const isInitialLoad = initialLoadRef.current;
      initialLoadRef.current = false;
      loadTarionDocuments(isInitialLoad);
    }
  }, [workspaceId, activeTab, selectedProjectId]);

  // CCP: reload on tab/project/workspace changes AND pagination/search changes
  useEffect(() => {
    if (!workspaceId || !selectedProjectId || activeTab !== 'ccp') return;
    loadCCPDocuments();
  }, [workspaceId, activeTab, selectedProjectId, ccpDebouncedSearchQuery, ccpPagination.page, ccpPagination.limit]);

  // Debounce CCP search — resets to page 1 on new search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCcpDebouncedSearchQuery(ccpSearchQuery);
      setCcpPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [ccpSearchQuery]);

  // Prevent browser's default drag behavior completely
  useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
    };

    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);

    return () => {
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const loadTarionDocuments = async (isInitialLoad = false) => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setReloadingDocuments(true);
      }
      const documents = await tarionDocumentService.getAll(workspaceId, selectedProjectId);
      setTarionDocuments(documents);
    } catch (error: any) {
      console.error('Error loading Tarion documents:', error);
      toast.error('Failed to load Tarion documents');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setReloadingDocuments(false);
      }
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const allFiles = Array.from(e.dataTransfer.files);
      const pdfFiles = allFiles.filter(file => file.type === 'application/pdf');

      if (pdfFiles.length === 0) {
        toast.error('Please drop only PDF files');
        return;
      }

      await uploadFiles(pdfFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (!workspaceId || !selectedProjectId) {
      toast.error('Please select a project first');
      return;
    }

    setUploading(true);

    // Initialize progress for all files
    const initialProgress: UploadProgress[] = files.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const
    }));
    setUploadProgressList(initialProgress);

    try {
      // Upload files sequentially to track individual progress
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        try {
          await tarionDocumentService.upload(
            workspaceId,
            selectedProjectId,
            file,
            (progressEvent: any) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );

              setUploadProgressList(prev =>
                prev.map((item, index) =>
                  index === i
                    ? { ...item, progress: percentCompleted }
                    : item
                )
              );
            }
          );

          // Mark as completed
          setUploadProgressList(prev =>
            prev.map((item, index) =>
              index === i
                ? { ...item, progress: 100, status: 'completed' as const }
                : item
            )
          );

          toast.success(`${file.name} uploaded successfully`);
        } catch (fileError: any) {
          console.error(`Error uploading ${file.name}:`, fileError);

          // Mark as error
          setUploadProgressList(prev =>
            prev.map((item, index) =>
              index === i
                ? {
                    ...item,
                    status: 'error' as const,
                    error: fileError.response?.data?.message || 'Upload failed'
                  }
                : item
            )
          );

          toast.error(`Failed to upload ${file.name}`);
        }
      }

      await loadTarionDocuments();

      // Clear progress list after a delay
      setTimeout(() => {
        setUploadProgressList([]);
      }, 3000);
    } catch (error: any) {
      console.error('Error uploading files:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleProcess = async (documentId: string) => {
    if (!workspaceId) return;

    setProcessingDocuments(prev => new Set(prev).add(documentId));

    try {
      const updatedDoc = await tarionDocumentService.process(workspaceId, documentId);
      toast.success('Document processed successfully');

      // Update the document in the list
      setTarionDocuments(prev =>
        prev.map(doc => (doc._id === documentId ? updatedDoc : doc))
      );
    } catch (error: any) {
      console.error('Error processing document:', error);
      toast.error(error.response?.data?.message || 'Failed to process document');

      // Reload to get updated status
      await loadTarionDocuments();
    } finally {
      setProcessingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const handleView = (url: string, fileName: string) => {
    setPdfModal({
      isOpen: true,
      url,
      fileName
    });
  };

  const handleDelete = (documentId: string, fileName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'localhost:3000 says',
      message: `Are you sure you want to delete "${fileName}"?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));

        if (!workspaceId) return;

        try {
          setDeletingDocumentId(documentId);
          await tarionDocumentService.delete(workspaceId, documentId);
          toast.success('Document deleted successfully');
          await loadTarionDocuments();
        } catch (error: any) {
          console.error('Error deleting document:', error);
          toast.error('Failed to delete document');
        } finally {
          setDeletingDocumentId(null);
        }
      }
    });
  };

  const handleToggleDocument = (documentId: string) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    if (selectedDocuments.size === tarionDocuments.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(tarionDocuments.map(doc => doc._id)));
    }
  };

  const handleProcessSelected = async () => {
    if (selectedDocuments.size === 0) {
      toast.info('Please select at least one document to process');
      return;
    }

    if (!workspaceId) return;

    const documentsToProcess = Array.from(selectedDocuments);

    // Filter out documents that are already completed or processing
    const validDocuments = documentsToProcess.filter(docId => {
      const doc = tarionDocuments.find(d => d._id === docId);
      return doc && doc.processingStatus !== 'completed' && doc.processingStatus !== 'processing';
    });

    if (validDocuments.length === 0) {
      toast.info('All selected documents are already processed or processing');
      return;
    }

    // Add all to processing set
    setProcessingDocuments(prev => new Set([...Array.from(prev), ...validDocuments]));

    try {
      // Process all documents in a single API call
      const result = await tarionDocumentService.processBulk(workspaceId, validDocuments);

      // Show result toast
      if (result.successful.length > 0) {
        toast.success(`${result.successful.length} document${result.successful.length > 1 ? 's' : ''} processed successfully`);
      }
      if (result.failed.length > 0) {
        toast.error(`${result.failed.length} document${result.failed.length > 1 ? 's' : ''} failed to process`);
      }

      // Reload documents
      await loadTarionDocuments();
    } catch (error: any) {
      console.error('Error processing documents:', error);
      toast.error(error.response?.data?.message || 'Failed to process documents');
      await loadTarionDocuments();
    } finally {
      // Clear processing set
      setProcessingDocuments(new Set());

      // Clear selection
      setSelectedDocuments(new Set());
    }
  };

  // ========== CCP DOCUMENT FUNCTIONS ==========

  const loadCCPDocuments = async (isInitialLoad = false) => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setReloadingDocuments(true);
      }
      const response = await ccpDocumentService.getAll(workspaceId, {
        projectId: selectedProjectId,
        page: ccpPagination.page,
        limit: ccpPagination.limit,
        search: ccpDebouncedSearchQuery || undefined
      });
      setCcpDocuments(response.documents);
      setCcpPagination(prev => ({
        ...prev,
        total: response.total,
        pages: response.pages
      }));
    } catch (error: any) {
      console.error('Error loading CCP documents:', error);
      toast.error('Failed to load CCP documents');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setReloadingDocuments(false);
      }
    }
  };

  const handleCCPFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    await uploadCCPFiles(Array.from(files));

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCCPDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Filter only PDF files
    const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      toast.error('Please drop only PDF files');
      return;
    }

    if (pdfFiles.length < files.length) {
      toast.warning(`${files.length - pdfFiles.length} non-PDF file(s) were skipped`);
    }

    await uploadCCPFiles(pdfFiles);
  };

  const uploadCCPFiles = async (files: File[]) => {
    if (!workspaceId || !selectedProjectId) {
      toast.error('Please select a project first');
      return;
    }

    setUploading(true);

    // Initialize upload progress for all files
    const initialProgress: UploadProgress[] = files.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const
    }));
    setUploadProgressList(initialProgress);

    // Upload files sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        await ccpDocumentService.upload(
          workspaceId,
          selectedProjectId,
          file,
          (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgressList(prev =>
              prev.map((upload, index) =>
                index === i
                  ? { ...upload, progress: percentCompleted }
                  : upload
              )
            );
          }
        );

        // Mark as completed
        setUploadProgressList(prev =>
          prev.map((upload, index) =>
            index === i
              ? { ...upload, progress: 100, status: 'completed' as const }
              : upload
          )
        );

        toast.success(`${file.name} uploaded successfully`);
      } catch (error: any) {
        console.error('Upload error:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Upload failed';

        // Mark as error
        setUploadProgressList(prev =>
          prev.map((upload, index) =>
            index === i
              ? { ...upload, status: 'error' as const, error: errorMessage }
              : upload
          )
        );

        toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
      }
    }

    setUploading(false);

    // Reset to page 1 and reload after uploads
    setCcpPagination(prev => ({ ...prev, page: 1 }));
    await loadCCPDocuments();

    // Clear upload progress after 3 seconds
    setTimeout(() => {
      setUploadProgressList([]);
    }, 3000);
  };

  const handleProcessCCP = async (documentId: string) => {
    if (!workspaceId) return;

    setProcessingDocuments(prev => new Set(prev).add(documentId));

    try {
      await ccpDocumentService.process(workspaceId, documentId);
      toast.success('Document processed successfully');

      // Remove from selectedDocuments when completed
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });

      await loadCCPDocuments();
    } catch (error: any) {
      console.error('Error processing document:', error);
      toast.error(error.response?.data?.message || 'Failed to process document');
      await loadCCPDocuments();
    } finally {
      setProcessingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const handleDeleteCCP = (documentId: string, fileName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete CCP Document',
      message: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      onConfirm: async () => {
        if (!workspaceId) return;

        try {
          setDeletingDocumentId(documentId);
          await ccpDocumentService.delete(workspaceId, documentId);
          toast.success('Document deleted successfully');
          await loadCCPDocuments();
        } catch (error: any) {
          console.error('Error deleting document:', error);
          toast.error('Failed to delete document');
        } finally {
          setDeletingDocumentId(null);
        }
      }
    });
  };

  const handleToggleCCPDocument = (documentId: string) => {
    // Check if document is already completed
    const doc = ccpDocuments.find(d => d._id === documentId);
    if (doc && doc.processingStatus === 'completed') {
      return; // Don't allow selecting completed documents
    }

    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  const handleToggleAllCCP = () => {
    // Only select documents that are not completed
    const selectableDocuments = ccpDocuments.filter(doc => doc.processingStatus !== 'completed');

    if (selectedDocuments.size === selectableDocuments.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(selectableDocuments.map(doc => doc._id)));
    }
  };

  const handleProcessSelectedCCP = async () => {
    if (selectedDocuments.size === 0) {
      toast.info('Please select at least one document to process');
      return;
    }

    if (!workspaceId) return;

    const documentsToProcess = Array.from(selectedDocuments);

    // Filter out documents that are already completed or processing
    const validDocuments = documentsToProcess.filter(docId => {
      const doc = ccpDocuments.find(d => d._id === docId);
      return doc && doc.processingStatus !== 'completed' && doc.processingStatus !== 'processing';
    });

    if (validDocuments.length === 0) {
      toast.info('All selected documents are already processed or processing');
      return;
    }

    // Add all to processing set
    setProcessingDocuments(prev => new Set([...Array.from(prev), ...validDocuments]));

    try {
      // Process all documents in a single API call
      const result = await ccpDocumentService.processBulk(workspaceId, validDocuments);

      // Show result toasts
      if (result.results.summary.successCount > 0) {
        toast.success(`${result.results.summary.successCount} document${result.results.summary.successCount > 1 ? 's' : ''} processed and assigned successfully`);
      }
      if (result.results.summary.unassignedCount > 0) {
        toast.warning(`${result.results.summary.unassignedCount} document${result.results.summary.unassignedCount > 1 ? 's' : ''} processed but no matching deal found`);
      }
      if (result.results.summary.failedCount > 0) {
        toast.error(`${result.results.summary.failedCount} document${result.results.summary.failedCount > 1 ? 's' : ''} failed to process`);
      }

      // Reload documents
      await loadCCPDocuments();
    } catch (error: any) {
      console.error('Error processing documents:', error);
      toast.error(error.response?.data?.message || 'Failed to process documents');
      await loadCCPDocuments();
    } finally {
      // Clear processing set
      setProcessingDocuments(new Set());

      // Clear selection
      setSelectedDocuments(new Set());
    }
  };

  const getCCPStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} className="status-icon-pending" />;
      case 'processing':
        return <Loader size={16} className="status-icon-processing spin" />;
      case 'completed':
        return <CheckCircle size={16} className="status-icon-completed" />;
      case 'failed':
        return <XCircle size={16} className="status-icon-failed" />;
      case 'unassigned':
        return <AlertCircle size={16} className="status-icon-unassigned" />;
      default:
        return null;
    }
  };

  const handleAssignCCP = (documentId: string, fileName: string) => {
    setAssignCCPModal({
      isOpen: true,
      documentId,
      documentFileName: fileName
    });
  };

  const handleCloseAssignModal = () => {
    setAssignCCPModal({
      isOpen: false,
      documentId: '',
      documentFileName: ''
    });
  };

  const handleAssignSuccess = async () => {
    // Remove assigned document from selectedDocuments
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      newSet.delete(assignCCPModal.documentId);
      return newSet;
    });

    handleCloseAssignModal();
    await loadCCPDocuments();
    toast.success('Document assigned to deal successfully');
  };

  const handleCCPPageChange = (page: number) => {
    setCcpPagination(prev => ({ ...prev, page }));
  };

  const handleCCPLimitChange = (limit: number) => {
    setCcpPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleCCPSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCcpSearchQuery(e.target.value);
  };

  // ========== END CCP DOCUMENT FUNCTIONS ==========

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    if (isNaN(bytes)) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} className="status-icon-pending" />;
      case 'processing':
        return <Loader size={16} className="status-icon-processing spin" />;
      case 'completed':
        return <CheckCircle size={16} className="status-icon-completed" />;
      case 'failed':
        return <XCircle size={16} className="status-icon-failed" />;
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'tarion', label: 'Tarion' },
    { id: 'ccp', label: 'CCP' }
  ];

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="page-container">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0 }}>Service Documents</h1>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0 0' }}>
              Manage Tarion and CCP documents
            </p>
          </div>
          {/* Project Selector */}
          {projects.length > 0 && (
            <div style={{ minWidth: '250px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '500',
                color: '#6B7280',
                marginBottom: '4px'
              }}>
                Project
              </label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="form-select"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #D4CBC4',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#48182F',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="page-content" style={{ marginTop: '24px' }}>
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              {/* Tabs */}
              <div className="portal-settings-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`portal-settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id as any)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tarion Tab Content */}
              {activeTab === 'tarion' && (
                <div className="portal-settings-section">
                  {/* Container with drag and drop handlers */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    {/* Upload Area */}
                    <div
                      className={`tarion-upload-zone ${isDragging ? 'dragging' : ''}`}
                      onClick={uploading ? undefined : () => fileInputRef.current?.click()}
                      style={{
                        border: `2px dashed ${isDragging ? '#48182F' : '#D4CBC4'}`,
                        borderRadius: '12px',
                        padding: '40px',
                        textAlign: 'center',
                        backgroundColor: isDragging ? '#F5F3F0' : '#F9FAFB',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        marginBottom: '24px',
                        transition: 'all 0.2s',
                        opacity: uploading ? 0.6 : 1
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />
                      <Upload size={48} color={isDragging ? '#48182F' : '#6B7280'} style={{ marginBottom: '16px' }} />
                      <p style={{ fontSize: '16px', fontWeight: '500', color: '#48182F', margin: '0 0 8px 0' }}>
                        Drop Tarion PDF files here or click to browse
                      </p>
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                        Supports multiple PDF files
                      </p>
                    </div>

                  {/* Upload Progress List */}
                  {uploadProgressList.length > 0 && (
                    <div style={{
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '24px'
                    }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#48182F',
                        margin: '0 0 12px 0'
                      }}>
                        Uploading Files...
                      </h4>
                      {uploadProgressList.map((upload, index) => (
                        <div key={index} style={{
                          marginBottom: index < uploadProgressList.length - 1 ? '12px' : '0'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '6px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                              <FileText size={16} color="#48182F" />
                              <span style={{
                                fontSize: '13px',
                                color: '#48182F',
                                fontWeight: '500',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {upload.fileName}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {upload.status === 'completed' && (
                                <CheckCircle size={16} color="#10B981" />
                              )}
                              {upload.status === 'error' && (
                                <XCircle size={16} color="#EF4444" />
                              )}
                              {upload.status === 'uploading' && (
                                <Loader size={16} color="#48182F" className="spin" />
                              )}
                              <span style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: upload.status === 'completed' ? '#10B981' :
                                  upload.status === 'error' ? '#EF4444' : '#48182F',
                                minWidth: '45px',
                                textAlign: 'right'
                              }}>
                                {upload.status === 'error' ? 'Failed' : `${upload.progress}%`}
                              </span>
                            </div>
                          </div>
                          {/* Progress Bar */}
                          <div style={{
                            width: '100%',
                            height: '6px',
                            backgroundColor: '#E5E7EB',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${upload.progress}%`,
                              height: '100%',
                              backgroundColor: upload.status === 'completed' ? '#10B981' :
                                upload.status === 'error' ? '#EF4444' : '#48182F',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          {upload.error && (
                            <p style={{
                              fontSize: '12px',
                              color: '#EF4444',
                              margin: '4px 0 0 0'
                            }}>
                              {upload.error}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bulk Actions */}
                  {tarionDocuments.length > 0 && selectedDocuments.size > 0 && (
                    <div style={{
                      marginBottom: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontSize: '14px', color: '#48182F', fontWeight: '500' }}>
                        {selectedDocuments.size} document{selectedDocuments.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-primary btn-md"
                        onClick={handleProcessSelected}
                        disabled={processingDocuments.size > 0}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {processingDocuments.size > 0 ? (
                          <>
                            <Loader size={16} className="spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            Process Selected
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Documents Table */}
                  {reloadingDocuments ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6B7280',
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '12px'
                    }}>
                      <div className="loading-spinner" style={{ marginBottom: '16px' }} />
                      <p style={{ fontSize: '16px', margin: 0 }}>Loading documents...</p>
                    </div>
                  ) : tarionDocuments.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="builders-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#F5F3F0' }}>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#48182F', width: '40px' }}>
                              <input
                                type="checkbox"
                                checked={selectedDocuments.size === tarionDocuments.length && tarionDocuments.length > 0}
                                onChange={handleToggleAll}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              File Name
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Size
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Uploaded Date
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Status
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Processed Date
                            </th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#48182F' }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {tarionDocuments.map((doc) => (
                            <tr key={doc._id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedDocuments.has(doc._id)}
                                  onChange={() => handleToggleDocument(doc._id)}
                                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                />
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FileText size={18} color="#48182F" />
                                  <span style={{ fontSize: '14px', color: '#48182F' }}>{doc.fileName}</span>
                                </div>
                              </td>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>
                                {formatFileSize(doc.size)}
                              </td>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>
                                {formatDate(doc.createdAt)}
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {getStatusIcon(doc.processingStatus)}
                                  <span style={{
                                    fontSize: '14px',
                                    textTransform: 'capitalize',
                                    color: doc.processingStatus === 'completed' ? '#10B981' :
                                      doc.processingStatus === 'failed' ? '#EF4444' :
                                        doc.processingStatus === 'processing' ? '#F59E0B' : '#6B7280'
                                  }}>
                                    {doc.processingStatus}
                                  </span>
                                </div>
                                {doc.processingError && (
                                  <p
                                    style={{ fontSize: '12px', color: '#EF4444', margin: '4px 0 0 0' }}
                                    title={doc.processingError}
                                  >
                                    Error: {doc.processingError.length > 80
                                      ? doc.processingError.substring(0, 80) + '...'
                                      : doc.processingError}
                                  </p>
                                )}
                              </td>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>
                                {doc.processedAt ? formatDate(doc.processedAt) : '-'}
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleProcess(doc._id)}
                                    disabled={
                                      processingDocuments.has(doc._id) ||
                                      doc.processingStatus === 'processing' ||
                                      doc.processingStatus === 'completed'
                                    }
                                    title="Process with AI"
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}
                                  >
                                    {processingDocuments.has(doc._id) || doc.processingStatus === 'processing' ? (
                                      <>
                                        <Loader size={16} className="spin" />
                                        Processing
                                      </>
                                    ) : (
                                      <>
                                        <Play size={16} />
                                        Process
                                      </>
                                    )}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleView(doc.url, doc.fileName)}
                                    title="View PDF"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', minWidth: '36px' }}
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(doc._id, doc.fileName)}
                                    disabled={deletingDocumentId === doc._id}
                                    title="Delete"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', minWidth: '36px' }}
                                  >
                                    {deletingDocumentId === doc._id ? (
                                      <Loader size={16} className="spin" />
                                    ) : (
                                      <Trash2 size={16} />
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6B7280',
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '12px'
                    }}>
                      <FileText size={48} color="#6B7280" style={{ marginBottom: '16px' }} />
                      <p style={{ fontSize: '16px', margin: 0 }}>No Tarion documents uploaded yet</p>
                      <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>
                        Upload PDF files using the area above
                      </p>
                    </div>
                  )}
                  </div>
                </div>
              )}

              {/* CCP Tab Content */}
              {activeTab === 'ccp' && (
                <div className="portal-settings-section">
                  {/* Container with drag and drop handlers */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleCCPDrop}
                  >
                    {/* Upload Area */}
                    <div
                      className={`tarion-upload-zone ${isDragging ? 'dragging' : ''}`}
                      onClick={uploading ? undefined : () => fileInputRef.current?.click()}
                      style={{
                        border: `2px dashed ${isDragging ? '#48182F' : '#D4CBC4'}`,
                        borderRadius: '12px',
                        padding: '40px',
                        textAlign: 'center',
                        backgroundColor: isDragging ? '#F5F3F0' : '#F9FAFB',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        marginBottom: '24px',
                        transition: 'all 0.2s',
                        opacity: uploading ? 0.6 : 1
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf"
                        onChange={handleCCPFileSelect}
                        style={{ display: 'none' }}
                      />
                      <Upload size={48} color={isDragging ? '#48182F' : '#6B7280'} style={{ marginBottom: '16px' }} />
                      <p style={{ fontSize: '16px', fontWeight: '500', color: '#48182F', margin: '0 0 8px 0' }}>
                        Drop CCP PDF files here or click to browse
                      </p>
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                        Supports multiple PDF files
                      </p>
                    </div>

                  {/* Upload Progress List */}
                  {uploadProgressList.length > 0 && (
                    <div style={{
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '24px'
                    }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#48182F',
                        margin: '0 0 12px 0'
                      }}>
                        Upload Progress
                      </h4>
                      {uploadProgressList.map((upload, index) => (
                        <div key={index} style={{
                          marginBottom: index < uploadProgressList.length - 1 ? '12px' : 0
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                              <FileText size={16} color="#48182F" />
                              <span style={{ fontSize: '14px', color: '#48182F', flex: 1 }}>{upload.fileName}</span>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                color: upload.status === 'completed' ? '#10B981' :
                                  upload.status === 'error' ? '#EF4444' : '#48182F',
                                minWidth: '45px',
                                textAlign: 'right'
                              }}>
                                {upload.status === 'error' ? 'Failed' : `${upload.progress}%`}
                              </span>
                            </div>
                          </div>
                          {/* Progress Bar */}
                          <div style={{
                            width: '100%',
                            height: '6px',
                            backgroundColor: '#E5E7EB',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${upload.progress}%`,
                              height: '100%',
                              backgroundColor: upload.status === 'completed' ? '#10B981' :
                                upload.status === 'error' ? '#EF4444' : '#48182F',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          {upload.error && (
                            <p style={{
                              fontSize: '12px',
                              color: '#EF4444',
                              margin: '4px 0 0 0'
                            }}>
                              {upload.error}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search Bar */}
                  <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                      <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6B7280', pointerEvents: 'none' }} />
                      <input
                        type="text"
                        placeholder="Search by file name..."
                        value={ccpSearchQuery}
                        onChange={handleCCPSearchChange}
                        style={{
                          width: '100%',
                          padding: '8px 36px 8px 36px',
                          fontSize: '14px',
                          border: '1px solid #D4CBC4',
                          borderRadius: '8px',
                          outline: 'none',
                          color: '#48182F',
                          backgroundColor: 'white',
                          boxSizing: 'border-box'
                        }}
                      />
                      {ccpSearchQuery && (
                        <button
                          onClick={() => setCcpSearchQuery('')}
                          style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0, display: 'flex', alignItems: 'center' }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {ccpPagination.total > 0 && (
                      <span style={{ fontSize: '13px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {ccpPagination.total} document{ccpPagination.total !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Bulk Actions */}
                  {ccpDocuments.length > 0 && selectedDocuments.size > 0 && (
                    <div style={{
                      marginBottom: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontSize: '14px', color: '#48182F', fontWeight: '500' }}>
                        {selectedDocuments.size} document{selectedDocuments.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-primary btn-md"
                        onClick={handleProcessSelectedCCP}
                        disabled={processingDocuments.size > 0}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {processingDocuments.size > 0 ? (
                          <>
                            <Loader size={16} className="spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            Process Selected
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Documents Table */}
                  {reloadingDocuments ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6B7280',
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '12px'
                    }}>
                      <div className="loading-spinner" style={{ marginBottom: '16px' }} />
                      <p style={{ fontSize: '16px', margin: 0 }}>Loading documents...</p>
                    </div>
                  ) : ccpDocuments.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="builders-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#F5F3F0' }}>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#48182F', width: '40px' }}>
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const selectableDocuments = ccpDocuments.filter(doc => doc.processingStatus !== 'completed');
                                  return selectedDocuments.size === selectableDocuments.length && selectableDocuments.length > 0;
                                })()}
                                onChange={handleToggleAllCCP}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              File Name
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Unit Number
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Size
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Uploaded Date
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#48182F' }}>
                              Status
                            </th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#48182F' }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {ccpDocuments.map((doc) => {
                            const isCompleted = doc.processingStatus === 'completed';
                            return (
                              <tr key={doc._id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedDocuments.has(doc._id)}
                                    onChange={() => handleToggleCCPDocument(doc._id)}
                                    disabled={isCompleted}
                                    style={{
                                      cursor: isCompleted ? 'not-allowed' : 'pointer',
                                      width: '16px',
                                      height: '16px',
                                      opacity: isCompleted ? 0.5 : 1
                                    }}
                                  />
                                </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FileText size={18} color="#48182F" />
                                  <span style={{ fontSize: '14px', color: '#48182F' }}>{doc.fileName}</span>
                                </div>
                              </td>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>
                                {doc.extractedData?.unitNumber || '-'}
                              </td>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>
                                {formatFileSize(doc.size)}
                              </td>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#6B7280' }}>
                                {formatDate(doc.createdAt)}
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {getCCPStatusIcon(doc.processingStatus)}
                                  <span style={{
                                    fontSize: '14px',
                                    textTransform: 'capitalize',
                                    color: doc.processingStatus === 'completed' ? '#10B981' :
                                      doc.processingStatus === 'failed' ? '#EF4444' :
                                        doc.processingStatus === 'processing' ? '#F59E0B' :
                                          doc.processingStatus === 'unassigned' ? '#F97316' : '#6B7280'
                                  }}>
                                    {doc.processingStatus}
                                  </span>
                                </div>
                                {doc.processingError && (
                                  <p
                                    style={{ fontSize: '12px', color: '#EF4444', margin: '4px 0 0 0' }}
                                    title={doc.processingError}
                                  >
                                    Error: {doc.processingError.length > 80
                                      ? doc.processingError.substring(0, 80) + '...'
                                      : doc.processingError}
                                  </p>
                                )}
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                  {doc.processingStatus === 'unassigned' ? (
                                    <button
                                      className="btn btn-warning btn-sm"
                                      onClick={() => handleAssignCCP(doc._id, doc.fileName)}
                                      title="Assign to Deal"
                                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}
                                    >
                                      <AlertCircle size={16} />
                                      Assign to Deal
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={() => handleProcessCCP(doc._id)}
                                      disabled={
                                        processingDocuments.has(doc._id) ||
                                        doc.processingStatus === 'processing' ||
                                        doc.processingStatus === 'completed'
                                      }
                                      title="Process with AI"
                                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}
                                    >
                                      {processingDocuments.has(doc._id) || doc.processingStatus === 'processing' ? (
                                        <>
                                          <Loader size={16} className="spin" />
                                          Processing
                                        </>
                                      ) : (
                                        <>
                                          <Play size={16} />
                                          Process
                                        </>
                                      )}
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleView(doc.url, doc.fileName)}
                                    title="View PDF"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', minWidth: '36px' }}
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDeleteCCP(doc._id, doc.fileName)}
                                    disabled={deletingDocumentId === doc._id || doc.processingStatus === 'completed'}
                                    title={doc.processingStatus === 'completed' ? 'Cannot delete completed documents' : 'Delete'}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', minWidth: '36px' }}
                                  >
                                    {deletingDocumentId === doc._id ? (
                                      <Loader size={16} className="spin" />
                                    ) : (
                                      <Trash2 size={16} />
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6B7280',
                      backgroundColor: '#F5F3F0',
                      border: '1px solid #D4CBC4',
                      borderRadius: '12px'
                    }}>
                      <FileText size={48} color="#6B7280" style={{ marginBottom: '16px' }} />
                      <p style={{ fontSize: '16px', margin: 0 }}>
                        {ccpDebouncedSearchQuery ? `No documents match "${ccpDebouncedSearchQuery}"` : 'No CCP documents uploaded yet'}
                      </p>
                      <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>
                        {ccpDebouncedSearchQuery ? 'Try a different search term' : 'Upload PDF files using the area above'}
                      </p>
                    </div>
                  )}

                  {/* Pagination Footer */}
                  {!reloadingDocuments && (ccpPagination.total > 0 || ccpDocuments.length > 0) && (
                    <div className="contacts-pagination-controls" style={{ marginTop: '16px' }}>
                      <div className="contacts-records-per-page">
                        <span>Show</span>
                        <select
                          value={ccpPagination.limit}
                          onChange={(e) => handleCCPLimitChange(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>records per page (Total: {ccpPagination.total})</span>
                      </div>
                      {ccpPagination.pages > 1 && (
                        <div className="contacts-pagination-wrapper">
                          <Pagination
                            current={ccpPagination.page}
                            total={ccpPagination.pages}
                            onPageChange={handleCCPPageChange}
                            maxWidth={400}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #F3F4F6;
          border-top-color: #48182F;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }

        .status-icon-pending {
          color: #6B7280;
        }

        .status-icon-processing {
          color: #F59E0B;
        }

        .status-icon-completed {
          color: #10B981;
        }

        .status-icon-failed {
          color: #EF4444;
        }

        .tarion-upload-zone:hover {
          border-color: #48182F !important;
          background-color: #F5F3F0 !important;
        }

        .builders-table tbody tr:hover {
          background-color: #F9FAFB;
        }
      `}</style>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="OK"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />

      {/* PDF Viewer Modal */}
      {pdfModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2200,
            padding: '20px'
          }}
          onClick={() => setPdfModal({ isOpen: false, url: '', fileName: '' })}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              height: '90%',
              maxWidth: '1200px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F9FAFB'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#48182F',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FileText size={20} />
                {pdfModal.fileName}
              </h3>
              <button
                onClick={() => setPdfModal({ isOpen: false, url: '', fileName: '' })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X size={24} color="#6B7280" />
              </button>
            </div>

            {/* PDF Viewer */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <iframe
                src={pdfModal.url}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title={pdfModal.fileName}
              />
            </div>
          </div>
        </div>
      )}

      {/* Assign CCP Modal */}
      <AssignCCPModal
        isOpen={assignCCPModal.isOpen}
        onClose={handleCloseAssignModal}
        onSuccess={handleAssignSuccess}
        workspaceId={workspaceId || ''}
        projectId={selectedProjectId}
        documentId={assignCCPModal.documentId}
        documentFileName={assignCCPModal.documentFileName}
      />
    </AppLayout>
  );
};

export default ServiceDocuments;
